import React, { useState, useCallback, useMemo } from 'react';
import { useLazyLoading } from '../../../hooks/utils/useIntersectionObserver';
import './LazyImage.css';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  thumbnailSrc?: string;
  placeholder?: string;
  onLoad?: () => void;
  onError?: (error?: Error) => void;
  style?: React.CSSProperties;
  /** Intersection Observer設定 */
  rootMargin?: string;
  threshold?: number;
  /** プリロード優先度 */
  priority?: 'high' | 'medium' | 'low';
  /** リトライ設定 */
  maxRetries?: number;
  retryDelay?: number;
}

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  thumbnailSrc,
  placeholder,
  onLoad,
  onError,
  style,
  rootMargin = '100px',
  threshold = 0.01,
  priority = 'medium',
  maxRetries = 3,
  retryDelay = 1000
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // 最適化されたIntersection Observer
  const { ref: imgRef, isIntersecting } = useLazyLoading({
    rootMargin,
    threshold,
    triggerOnce: true
  });

  // RefをHTMLDivElementにキャスト
  const containerRef = imgRef as React.RefObject<HTMLDivElement>;

  // 画像読み込み成功時の処理
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
    setRetryCount(0);
    setIsRetrying(false);
    onLoad?.();
  }, [onLoad]);

  // エラー時のリトライ処理
  const handleImageError = useCallback((event?: React.SyntheticEvent<HTMLImageElement>) => {
    const error = new Error(`Failed to load image: ${src}`);
    
    if (retryCount < maxRetries) {
      setIsRetrying(true);
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setIsRetrying(false);
        // Force reload by adding cache-busting parameter
        const img = event?.target as HTMLImageElement;
        if (img && img.src) {
          const url = new URL(img.src);
          url.searchParams.set('retry', retryCount.toString());
          img.src = url.toString();
        }
      }, retryDelay * Math.pow(2, retryCount)); // Exponential backoff
    } else {
      setHasError(true);
      setIsRetrying(false);
      onError?.(error);
    }
  }, [src, retryCount, maxRetries, retryDelay, onError]);

  // 使用する画像URL（サムネイル優先、リトライ対応）
  const imageUrl = useMemo(() => {
    const baseUrl = thumbnailSrc || src;
    
    // baseUrlの安全性チェック
    if (!baseUrl || typeof baseUrl !== 'string') {
      console.warn(`[LazyImage] Invalid baseUrl:`, baseUrl);
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
    }
    
    // URL検証とリトライ処理
    if (retryCount > 0) {
      try {
        // data:URIの場合はリトライパラメータを追加しない
        if (baseUrl.startsWith('data:')) {
          return baseUrl;
        }
        
        // 相対URLの場合はリトライパラメータを文字列として追加
        if (baseUrl.startsWith('/') || baseUrl.startsWith('./') || baseUrl.startsWith('../')) {
          const separator = baseUrl.includes('?') ? '&' : '?';
          return `${baseUrl}${separator}retry=${retryCount}`;
        }
        
        // 絶対URLの場合のみnew URL()を使用
        if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
          const url = new URL(baseUrl);
          url.searchParams.set('retry', retryCount.toString());
          return url.toString();
        }
        
        // その他の場合は文字列として処理
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}retry=${retryCount}`;
        
      } catch (urlError) {
        console.warn(`[LazyImage] URL construction failed for: ${baseUrl}`, urlError);
        // フォールバック：文字列としてリトライパラメータを追加
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}retry=${retryCount}`;
      }
    }
    
    return baseUrl;
  }, [thumbnailSrc, src, retryCount]);

  // 優先度に基づく読み込み属性
  const loadingPriority = useMemo(() => {
    switch (priority) {
      case 'high':
        return 'eager';
      case 'low':
        return 'lazy';
      default:
        return 'lazy';
    }
  }, [priority]);

  // styleプロップを画像用とコンテナ用に分離
  const imageStyle = useMemo(() => {
    const imgStyles: React.CSSProperties = {
      opacity: isLoaded ? 1 : 0,
      transition: 'opacity 0.3s ease-in-out'
    };

    // 親から渡されたstyleプロップから画像関連のスタイルを抽出
    if (style) {
      if (style.objectFit) imgStyles.objectFit = style.objectFit;
      if (style.objectPosition) imgStyles.objectPosition = style.objectPosition;
      if (style.width) imgStyles.width = style.width;
      if (style.height) imgStyles.height = style.height;
    }

    return imgStyles;
  }, [isLoaded, style]);

  // コンテナ用のスタイル（画像関連のスタイルを除く）
  const containerStyle = useMemo(() => {
    if (!style) return undefined;

    const containerStyles = { ...style };
    delete containerStyles.objectFit;
    delete containerStyles.objectPosition;
    // width, heightは両方に適用する必要がある場合があるので削除しない

    return containerStyles;
  }, [style]);

  return (
    <div
      ref={containerRef}
      className={`lazy-image-container ${className} ${priority}`}
      style={containerStyle}
      data-priority={priority}
      data-retry-count={retryCount}
    >
      {/* プレースホルダー（可視範囲外） */}
      {!isIntersecting && (
        <div className="lazy-placeholder">
          {placeholder ? (
            <div className="custom-placeholder">{placeholder}</div>
          ) : (
            <div className="skeleton-shimmer"></div>
          )}
        </div>
      )}

      {/* エラー表示 */}
      {hasError && !isRetrying && (
        <div className="lazy-error">
          <div className="error-icon">📷</div>
          <div className="error-content">
            <span className="error-message">画像を読み込めませんでした</span>
            <span className="error-detail">
              Retries: {retryCount}/{maxRetries}
            </span>
          </div>
        </div>
      )}

      {/* リトライ中表示 */}
      {isRetrying && (
        <div className="lazy-retrying">
          <div className="retry-spinner">
            <div className="spinner"></div>
          </div>
          <span className="retry-message">
            再試行中... ({retryCount}/{maxRetries})
          </span>
        </div>
      )}

      {/* 実際の画像 */}
      {isIntersecting && !hasError && !isRetrying && (
        <>
          {/* ローディング中のぼかし背景 */}
          {!isLoaded && (
            <div className="lazy-loading">
              <div className="loading-shimmer"></div>
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
              <div className="loading-progress">
                Loading{priority === 'high' ? ' (Priority)' : ''}...
              </div>
            </div>
          )}
          
          {/* メイン画像 */}
          <img
            src={imageUrl}
            alt={alt}
            className={`lazy-image ${isLoaded ? 'loaded' : 'loading'} priority-${priority}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading={loadingPriority as 'eager' | 'lazy'}
            decoding="async"
            style={imageStyle}
          />
        </>
      )}
    </div>
  );
};

export default LazyImage;